import React, { useEffect, useState, useCallback } from "react";
import { get_white_list } from "../apis/GetWhiteList";
import { get_white_by_id } from "../apis/GetWhiteById";
import { add_white } from "../apis/AddWhite";
import { modify_white } from "../apis/ModifyWhite";
import { delete_white } from "../apis/DeleteWhite";
import Loading from "../pages/Loading";
import ConfirmToast from "../pages/Toast";
import NotificationToast from "../Component/Toast";
import { useTranslation } from "../contexts/LocaleContext";
import PaginationFooter from "./PaginationFooter";
import AddWhiteModal from "./AddWhiteModal";
import "../css/user.scss";
import Cookies from "js-cookie";
import { setAPIAuthorizationToken } from "../apis/API";

const Whitelist = () => {
  const { t } = useTranslation();
  const [whitelist, setWhitelist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showWhiteModal, setShowWhiteModal] = useState(false);
  const [editingWhite, setEditingWhite] = useState(null);
  const [confirmDeleteWhite, setConfirmDeleteWhite] = useState(null);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [goToInput, setGoToInput] = useState("1");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  //获取白名单列表
  const fetchWhitelist = useCallback(
    async (
      page = 1,
      suppressLoading = false,
      name = "",
      perPage = pageSize,
      status = statusFilter
    ) => {
      try {
        if (!suppressLoading) setIsLoading(true);
        const res = await get_white_list(page, perPage, name, status);
        let items = [];
        let total = 0;
        if (res && res.data) {
          items = res.data.data || [];
          total = res.data.total || items.length;
        }
        setWhitelist(items);
        setTotalItems(Number(total) || items.length);
      } catch (err) {
        console.error("Failed to fetch whitelist:", err);
      } finally {
        if (!suppressLoading) setIsLoading(false);
      }
    },
    [pageSize, statusFilter]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
    // initial fetch (do not suppress loading)
    fetchWhitelist(1, false, "", pageSize, statusFilter);
  }, [fetchWhitelist, pageSize, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setGoToInput(String(currentPage));
  }, [currentPage]);

  const goToPage = async (page) => {
    const p = Math.max(1, Math.min(totalPages, Number(page) || 1));
    setCurrentPage(p);
    await fetchWhitelist(p, false, searchName, pageSize, statusFilter);
  };

  const showNotification = (message, type = "success") =>
    setToastInfo({ message, type });

  const openEditModal = async (white) => {
    const id = white?.id ?? white?._id ?? white;
    if (!id) {
      setEditingWhite(white || null);
      setShowWhiteModal(true);
      return;
    }
    try {
      setIsLoading(true);
      const res = await get_white_by_id(id);
      const data = res && res.data ? res.data : null;
      const item = data && data.data ? data.data : data;
      setEditingWhite(item || null);
      setShowWhiteModal(true);
    } catch (err) {
      console.error("Failed to fetch whitelist by id:", err);
      showNotification("Failed to load whitelist details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeWhiteModal = () => {
    setShowWhiteModal(false);
    setEditingWhite(null);
  };

  // handle submit from modal (parent performs API calls)
  const handleWhiteSubmit = async (form) => {
    setIsSaving(true);
    try {
      if (form.id) {
        // include updateTime when modifying existing whitelist
        const updateTime = new Date().toISOString();
        await modify_white(
          form.id,
          form.urlOrAddress,
          form.description,
          updateTime
        );
        showNotification("Whitelist updated", "success");
      } else {
        await add_white(form.urlOrAddress, form.description);
        showNotification("Whitelist added", "success");
      }
      await fetchWhitelist(currentPage, true, searchName, pageSize);
      closeWhiteModal();
    } catch (err) {
      console.error("Failed to save whitelist:", err);
      showNotification("Failed to save whitelist", "error");
    } finally {
      setIsSaving(false);
    }
    await fetchWhitelist(currentPage, true, searchName, pageSize, statusFilter);
  };

  const formatDate = (iso) => {
    if (!iso) return "2026-01-04 11:24";
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch {
      return iso;
    }
  };

  return (
    <div className="users-page ">
      <div className="users-controls-card">
        <div className="controls-left">
          <div className="search-input">
            <input
              placeholder={
                t ? t("whitelist.search_placeholder") : "Search here"
              }
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  // show loading overlay while backend responds
                  fetchWhitelist(1, false, searchName, pageSize, statusFilter);
                }
              }}
            />
            <button
              type="button"
              className="search-icon"
              aria-label="Search"
              onClick={() => {
                setCurrentPage(1);
                // show loading overlay while backend responds
                fetchWhitelist(1, false, searchName, pageSize, statusFilter);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="m16.875 15.455 4 4.005a.5.5 0 0 1-.02.691l-.7.7a.5.5 0 0 1-.71 0l-4-4.004a2.6 2.6 0 0 1-.3-.36l-.75-1.001a7 7 0 0 1-4.37 1.531A7.011 7.011 0 0 1 6.973 3.69a6.995 6.995 0 0 1 8.518 1.971 7.015 7.015 0 0 1-.046 8.753l1 .691q.234.151.43.35M5.025 10.01a5.003 5.003 0 0 0 5 5.006 5 5 0 0 0 3.535-1.466 5 5 0 0 0 1.465-3.54 5.003 5.003 0 0 0-5-5.006c-2.761 0-5 2.241-5 5.006"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="controls-right">
          <button className="btn-add" onClick={() => setShowWhiteModal(true)}>
            {t ? t("whitelist.add") : "Add whitelist"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : whitelist.length === 0 ? (
        <div>{t ? t("whitelist.no_items") : "No whitelist items"}</div>
      ) : (
        <div className="users-table-card">
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t ? t("whitelist.table.url") : "URL/DNS/IP"}</th>
                  <th>
                    {t ? t("whitelist.table.description") : "Description"}
                  </th>
                  <th>{t ? t("whitelist.table.date") : "Date"}</th>
                  <th>{t ? t("whitelist.table.operation") : "Operation"}</th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map((item, idx) => {
                  const id = item.id ?? item._id ?? idx;
                  return (
                    <tr key={id}>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("whitelist.table.url") : "URL/DNS/IP"}
                          </span>
                          <span className="cell-value">
                            {item.urlOrAddress ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t
                              ? t("whitelist.table.description")
                              : "Description"}
                          </span>
                          <span className="cell-value">
                            {item.description ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("whitelist.table.date") : "Date"}
                          </span>
                          <span className="cell-value">
                            {formatDate(item.updateTime)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-value">
                            <div className="op-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => {
                                  openEditModal(item);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="1em"
                                  height="1em"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="#000"
                                    fillRule="evenodd"
                                    d="m19.902 3.44.659.658c.28.28.439.66.439 1.058v.758c.005.404-.153.793-.44 1.078l-1.845 1.816a.5.5 0 0 1-.708 0l-2.804-2.844a.5.5 0 0 1 0-.699L17.01 3.44A1.5 1.5 0 0 1 18.087 3h.758c.397 0 .777.158 1.057.44m-3.082 7.245a.5.5 0 0 0 0-.7l-2.854-2.813a.5.5 0 0 0-.708 0l-8.052 8.053a2 2 0 0 0-.449.679l-1.716 4.331a.5.5 0 0 0 .19.619.5.5 0 0 0 .538.11l4.33-1.757c.255-.101.486-.254.679-.449z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => {
                                  setConfirmDeleteWhite(id);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="1em"
                                  height="1em"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="#000"
                                    fillRule="evenodd"
                                    d="M20 5.5v-1a.5.5 0 0 0-.5-.5H15V3a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1H4.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5M7.72 18h8.56L17 8h2l-.85 12.14a2 2 0 0 1-2 1.86H7.87a2 2 0 0 1-2-1.86L5 8h2z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="users-table-footer" style={{ marginTop: 12 }}>
            <PaginationFooter
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
                fetchWhitelist(1, false, searchName, n, statusFilter);
              }}
              currentPage={currentPage}
              onPageChange={(p) => goToPage(p)}
              totalPages={totalPages}
              goToValue={goToInput}
              onGoToValueChange={(v) => setGoToInput(v)}
              onGoClick={() => goToPage(goToInput)}
            />
          </div>
        </div>
      )}
      {/* Whitelist add/edit modal */}
      <AddWhiteModal
        isOpen={showWhiteModal}
        onClose={closeWhiteModal}
        initialData={editingWhite || undefined}
        onSubmit={handleWhiteSubmit}
        isLoading={isSaving}
      />
      {/* confirm delete toast */}
      <ConfirmToast
        show={!!confirmDeleteWhite}
        onClose={() => setConfirmDeleteWhite(null)}
        confirmProps={{
          title: t ? t("confirm.delete_whitelist.title") : "Delete Whitelist",
          description: confirmDeleteWhite
            ? t
              ? t("confirm.delete_whitelist.description")
              : "Are you sure you want to delete this whitelist item?"
            : "",
          confirmLabel: t ? t("confirm.delete") : "Delete",
          cancelLabel: t ? t("confirm.cancel") : "Cancel",
          onConfirm: async () => {
            try {
              setIsSaving(true);
              if (confirmDeleteWhite) {
                await delete_white(confirmDeleteWhite);
                showNotification(
                  t ? t("notification.whitelist_deleted") : "Whitelist deleted",
                  "success"
                );
                await fetchWhitelist(
                  currentPage,
                  false,
                  searchName,
                  pageSize,
                  statusFilter
                );
              }
            } catch (err) {
              console.error("Failed to delete whitelist:", err);
              showNotification(
                t
                  ? t("notification.failed_delete")
                  : "Failed to delete whitelist",
                "error"
              );
            } finally {
              setIsSaving(false);
              setConfirmDeleteWhite(null);
            }
          },
          onCancel: () => setConfirmDeleteWhite(null),
        }}
      />
      {/* top notification */}
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
    </div>
  );
};

export default Whitelist;
